import { renderMarkdown, sanitizeHtml } from 'tools/safeHtml';
import { IDialogItem, ISourceReports, JsonBlock, SearchResults, TableData } from '../types';
import { cutFiltersApplied } from './filters-applied';
import { relabelsAsSearchTurn, stripHtmlTags } from './index';

export interface CopyContent {
    html: string;
    text: string;
}

export interface CopyOptions {
    origin: string;
    sourcesLabel: string;
}

interface Link {
    name: string;
    url: string;
}

const MARKDOWN_IMAGE_REGEX = /!\[[^\]]*]\([^)]*\)/g;

const escapeHtml = (value: string) =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const absoluteUrl = (url: string, origin: string) => (url.startsWith('/') ? origin + url : url);

function cleanHtml(html: string, origin: string): string {
    const doc = new DOMParser().parseFromString(sanitizeHtml(html), 'text/html');
    doc.querySelectorAll('img, picture').forEach((node) => node.remove());
    doc.querySelectorAll('a[href]').forEach((node) => {
        node.setAttribute('href', absoluteUrl(node.getAttribute('href') ?? '', origin));
    });
    return doc.body.innerHTML;
}

function tableColumns(table: TableData): { key: string; label: string }[] {
    if (table.columns?.length) return table.columns;
    return Object.keys(table.data[0] ?? {}).map((key) => ({ key, label: key }));
}

function tableHtml(table: TableData): string {
    const columns = tableColumns(table);
    const head = columns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('');
    const rows = table.data
        .map(
            (row) =>
                `<tr>${columns.map((column) => `<td>${escapeHtml(String(row[column.key] ?? ''))}</td>`).join('')}</tr>`,
        )
        .join('');
    return `<table border="1" cellpadding="4" cellspacing="0"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table>`;
}

function tableMarkdown(table: TableData): string {
    const columns = tableColumns(table);
    const cell = (value: unknown) =>
        String(value ?? '')
            .replace(/\|/g, '\\|')
            .replace(/\r?\n/g, ' ');
    const line = (cells: string[]) => `| ${cells.join(' | ')} |`;
    return [
        line(columns.map((column) => cell(column.label))),
        line(columns.map(() => '---')),
        ...table.data.map((row) => line(columns.map((column) => cell(row[column.key])))),
    ].join('\n');
}

function linksHtml(links: Link[]): string {
    const items = links.map((link) =>
        link.url
            ? `<li><a href="${escapeHtml(link.url)}">${escapeHtml(link.name)}</a></li>`
            : `<li>${escapeHtml(link.name)}</li>`,
    );
    return `<ul>${items.join('')}</ul>`;
}

const linksText = (links: Link[]) =>
    links.map((link) => (link.url ? `- ${link.name}: ${link.url}` : `- ${link.name}`)).join('\n');

/**
 * The response as the user reads it, for the clipboard: text, tables and source links in order. Images, charts,
 * the "Filters Applied" block, progress, thinking, related reports and other panel chrome are left out.
 */
export function buildCopyContent(item: IDialogItem, options: CopyOptions): CopyContent {
    const html: string[] = [];
    const text: string[] = [];
    const sources: Link[] = [];
    let pendingMarkdown = '';

    const flushMarkdown = () => {
        let markdown = pendingMarkdown;
        for (let cut = cutFiltersApplied(markdown); cut; cut = cutFiltersApplied(markdown)) {
            markdown = cut.body;
        }
        markdown = markdown
            .replace(MARKDOWN_IMAGE_REGEX, '')
            .replace(/\]\((\/[^)\s]*)\)/g, (_, url) => `](${options.origin}${url})`)
            .trim();
        pendingMarkdown = '';
        if (!markdown) return;
        html.push(cleanHtml(renderMarkdown(markdown), options.origin));
        text.push(markdown);
    };

    const pushBlock = (block: JsonBlock) => {
        if ('error' in block) return;

        if (block.data_type === 'table') {
            const table = block as TableData;
            if (!table.data?.length) return;
            html.push(tableHtml(table));
            text.push(tableMarkdown(table));
            return;
        }

        if (block.data_type === 'search_results') {
            const search = block as SearchResults;
            const links = (search.data ?? []).map((row) => ({
                name: row.name,
                url: absoluteUrl(row.url || row.external_link || '', options.origin),
            }));
            if (relabelsAsSearchTurn(search) && search.search_criteria_summary_phrase) {
                html.push(`<p>${escapeHtml(search.search_criteria_summary_phrase)}</p>`);
                text.push(search.search_criteria_summary_phrase);
            }
            if (links.length) {
                html.push(linksHtml(links));
                text.push(linksText(links));
            }
            return;
        }

        if (block.data_type === 'source_reports') {
            (block as ISourceReports).reports.forEach((report) => {
                if (!sources.some((source) => source.name === report.name)) {
                    sources.push({ name: report.name, url: report.url ? absoluteUrl(report.url, options.origin) : '' });
                }
            });
        }
    };

    item.parts.forEach((part) => {
        if (part.type === 'markdown') {
            pendingMarkdown += part.message;
            return;
        }
        flushMarkdown();
        if (part.type === 'html' && part.message.trim()) {
            const clean = cleanHtml(part.message, options.origin);
            html.push(clean);
            text.push(stripHtmlTags(clean).trim());
        } else if (part.type === 'json') {
            part.jsonBlocks?.forEach(pushBlock);
        }
    });
    flushMarkdown();

    if (sources.length) {
        html.push(`<p><strong>${escapeHtml(options.sourcesLabel)}</strong></p>${linksHtml(sources)}`);
        text.push(`${options.sourcesLabel}:\n${linksText(sources)}`);
    }

    return { html: html.join('\n'), text: text.filter(Boolean).join('\n\n') };
}

function copyWithCommand(content: CopyContent): boolean {
    const onCopy = (event: ClipboardEvent) => {
        event.clipboardData?.setData('text/html', content.html);
        event.clipboardData?.setData('text/plain', content.text);
        event.preventDefault();
    };
    const holder = document.createElement('textarea');
    holder.value = content.text;
    holder.setAttribute('readonly', '');
    holder.style.position = 'fixed';
    holder.style.opacity = '0';
    document.body.appendChild(holder);
    holder.select();
    document.addEventListener('copy', onCopy);
    try {
        return document.execCommand('copy');
    } catch {
        return false;
    } finally {
        document.removeEventListener('copy', onCopy);
        holder.remove();
    }
}

/**
 * Puts both flavors on the clipboard. Outside a secure context or in an iframe without clipboard-write permission
 * the async API is missing or refuses, and the legacy copy command takes over.
 */
export async function writeCopyContent(content: CopyContent): Promise<boolean> {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        try {
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([content.html], { type: 'text/html' }),
                    'text/plain': new Blob([content.text], { type: 'text/plain' }),
                }),
            ]);
            return true;
        } catch {
            // falls through to the legacy command
        }
    }
    return copyWithCommand(content);
}
